import { Box, Circle, Spacer, Stack, Text } from '@chakra-ui/react'
import type { DateTime } from 'luxon'

import { getRelativeTimeShort } from '@/utils/time'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import getNotificationBody from '@/client/services/worlds/notifications/getNotificationBody'
import getNotificationUrl from '@/client/services/worlds/notifications/getNotificationUrl'
import type { NotificationType } from '@/client/services/worlds/notifications/types'

import ShwLink from '@/components/ShwLink'
import type { NotificationDetails } from '@/components/Worlds/types'
import { withErrorBoundary } from '@/components/Worlds/UI/ErrorBoundary'

import { NOTIFICATION_ITEM_HEIGHT } from './constants'
import NotificationAvatar from './NotificationAvatar'
import NotificationError from './NotificationError'

function NotificationItemInternal({
  notification,
  lastViewedAt,
  onClick,
}: {
  notification: NotificationDetails<NotificationType>
  lastViewedAt: DateTime | null
  onClick?: () => void
}): JSX.Element | null {
  const { notifyAt, user, relatedUser, clickedAt, id, world, payload } = notification

  const [onClickNotification] = useAPIWrapper(
    async () => {
      await RPC.markNotificationAsClicked({ id }, { disableProgressBar: true })
      onClick?.()
    },
    [id, onClick],
    'Click Notification'
  )
  const isNew = lastViewedAt == null || notifyAt > lastViewedAt

  const body = getNotificationBody({
    type: payload.type,
    notification,
  })

  return (
    <ShwLink
      href={getNotificationUrl({ type: payload.type, notification })}
      onClick={onClickNotification}
      width="100%"
      height={NOTIFICATION_ITEM_HEIGHT}
    >
      <Box
        padding="12px"
        borderRadius="8px"
        bg={isNew ? 'primary.subtle' : 'bg'}
        _hover={{ bg: isNew ? 'primary.muted' : 'bg.muted' }}
        _active={{ bg: isNew ? 'primary.muted' : 'bg.emphasized' }}
        fontSize="14px"
        width="100%"
      >
        <Stack direction="row" align="center" spacing="12px">
          <NotificationAvatar user={user} relatedUser={relatedUser} world={world} />
          <Box flex={1}>
            <Text variant="supporting">
              {body.map(({ text, emphasized = false }) => {
                if (emphasized) {
                  return <b key={text}>{text}</b>
                }
                return text
              })}
            </Text>
          </Box>
          <Stack
            direction="column"
            justify="space-between"
            spacing="8px"
            align="flex-end"
            alignSelf="stretch"
          >
            {clickedAt == null ? <Circle bg="primary.solid" size="12px" /> : <Spacer />}
            <Text as="span" color="fg.muted" variant="supporting">
              {getRelativeTimeShort({ time: notifyAt })}
            </Text>
          </Stack>
        </Stack>
      </Box>
    </ShwLink>
  )
}

const NotificationItem = withErrorBoundary(NotificationItemInternal, () => <NotificationError />)

export default NotificationItem
